import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import api, { customersAPI, projectsAPI, tenantAPI } from "../../../api/client";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermission } from "../../../hooks/usePermission";
import useAuth from "../../../hooks/useAuth";
import useStatusColors from "../../../hooks/useStatusColors";
import { hexToRgba } from "../../../utils/statusColors";
import { getCurrentStageNames } from "../../../utils/currentStage";
import { todayString } from "../../../utils/dateUtils";
import ProjectCard from "../../../components/projects/ProjectCard";
import AddProjectModal from "../../../components/projects/AddProjectModal";
import EditProjectModal from "../../../components/projects/EditProjectModal";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import PortfolioGanttChart from "../../../components/gantt/PortfolioGanttChart";
import ViewModeSwitch, {
  useViewModeParam,
} from "../../../components/gantt/ViewModeSwitch";
import "../../../components/projects/Projects.css";
import {
  PROJECT_STATUS_OPTIONS,
  formatProjectStatus,
} from "../../../utils/formatProjectStatus";

function formatDate(dateValue) {
  if (!dateValue) return "-";
  return new Date(dateValue).toLocaleDateString();
}

function formatUserName(user) {
  if (!user) return "-";
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || "-";
}

const STATUS_OPTIONS = PROJECT_STATUS_OPTIONS;

const FILTER_STORAGE_KEY = "projectsPageFilters";

const PROJECT_VIEW_MODES = ["card", "list", "gantt"];
// The Gantt covers the whole project list, so folders offer cards and list only.
const FOLDER_VIEW_MODES = ["card", "list"];

function readStoredFilters() {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStoredFilters(filters) {
  try {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore storage errors
  }
}

// Returning from a project detail page remounts this page, so the loaded page
// count and scroll offset are restored from the last visit in this tab.
const RESTORE_STORAGE_KEY = "projectsPageRestore";
const MAX_RESTORE_COUNT = 200;

function readStoredRestore() {
  try {
    const raw = sessionStorage.getItem(RESTORE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed) return null;
    const count = Number(parsed.count) || 0;
    const scrollTop = Number(parsed.scrollTop) || 0;
    if (count <= 0 && scrollTop <= 0) return null;
    return { count: Math.min(count, MAX_RESTORE_COUNT), scrollTop };
  } catch {
    return null;
  }
}

function saveStoredRestore(restore) {
  try {
    sessionStorage.setItem(RESTORE_STORAGE_KEY, JSON.stringify(restore));
  } catch {
    // ignore storage errors
  }
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = usePermission(PERMISSIONS.PROJECT_CREATE);
  const canDelete = usePermission(PERMISSIONS.PROJECT_DELETE);
  const canViewAll = usePermission(PERMISSIONS.PROJECT_VIEW_ALL);
  const canAssignUsers = usePermission(PERMISSIONS.PROJECT_ASSIGN_USERS);
  const { resolvedColors } = useStatusColors();

  const PROJECT_STATUS_FALLBACK = {
    COMPLETED: "#bbf7d0",
    ON_HOLD: "#fecaca",
    AWAITING_INVOICE: "#fde68a",
  };

  const getRowBackground = (status) => {
    if (resolvedColors[status]) return hexToRgba(resolvedColors[status], 0.2);
    return PROJECT_STATUS_FALLBACK[status] || "transparent";
  };

  const [projects, setProjects] = useState([]);
  const [myActionProjects, setMyActionProjects] = useState([]);
  const [folders, setFolders] = useState([]);

  const storedFilters = useMemo(() => readStoredFilters(), []);

  const [activeTab, setActiveTab] = useState(
    storedFilters.activeTab ?? "projects",
  );
  const [activeFolderId, setActiveFolderId] = useState(
    storedFilters.activeFolderId ?? null,
  );
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const [search, setSearch] = useState(storedFilters.search ?? "");
  const [statuses, setStatuses] = useState(storedFilters.statuses ?? []);
  const [workflows, setWorkflows] = useState(storedFilters.workflows ?? []);
  const [customers, setCustomers] = useState(storedFilters.customers ?? []);
  const [sortBy, setSortBy] = useState(storedFilters.sortBy ?? "updatedAt");
  const [showMyProjectsAtTop, setShowMyProjectsAtTop] = useState(
    storedFilters.showMyProjectsAtTop ?? true,
  );
  const [limit] = useState(10);
  const [totalProjects, setTotalProjects] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");
  const scrollBodyRef = useRef(null);
  const sentinelRef = useRef(null);

  const storedRestore = useMemo(() => readStoredRestore(), []);
  // Consumed by the first load() only; filter changes must not re-restore.
  const pendingRestoreRef = useRef(storedRestore);
  const pendingScrollRef = useRef(null);

  const [copyTargetFolderId, setCopyTargetFolderId] = useState("");
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySearch, setCopySearch] = useState("");
  const [copySelectionIds, setCopySelectionIds] = useState([]);
  const [copyingToFolder, setCopyingToFolder] = useState(false);
  const [allProjectsForCopy, setAllProjectsForCopy] = useState([]);
  const [loadingCopyProjects, setLoadingCopyProjects] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [folderForm, setFolderForm] = useState({ name: "", description: "" });
  const [projectCardFields, setProjectCardFields] = useState([]);
  const [projectListFields, setProjectListFields] = useState([]);
  const [cardFieldOrder, setCardFieldOrder] = useState(null);
  const [requestedView, setViewMode] = useViewModeParam(PROJECT_VIEW_MODES);
  // The Gantt replaces the scrolling list, so effects bound to the list's
  // scroll container must re-attach whenever it comes back.
  const listMounted = !(requestedView === "gantt" && activeTab === "projects");
  const [inlineAddOpen, setInlineAddOpen] = useState(false);
  const [creatingInline, setCreatingInline] = useState(false);
  const [createWorkflowOptions, setCreateWorkflowOptions] = useState([]);
  const [createCustomerOptions, setCreateCustomerOptions] = useState([]);
  const [inlineCreateForm, setInlineCreateForm] = useState({
    name: "",
    customerId: "",
    workflowId: "",
  });
  const [inlineCustomFieldValues, setInlineCustomFieldValues] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const skip = 0;
      const pendingRestore = pendingRestoreRef.current;
      const fetchLimit = Math.max(limit, pendingRestore?.count || 0);
      const folderId =
        activeTab === "folders" && activeFolderId ? activeFolderId : undefined;
      const [projectsResp, tenantResp, foldersResp, myActionResp] =
        await Promise.all([
          projectsAPI.getAll({
            search: search || undefined,
            statuses: statuses.length ? statuses.join(",") : undefined,
            workflows: workflows.length ? workflows.join(",") : undefined,
            customers: customers.length ? customers.join(",") : undefined,
            sortBy,
            folderId,
            skip,
            limit: fetchLimit,
          }),
          tenantAPI.getTenant(),
          canViewAll ? projectsAPI.getFolders() : Promise.resolve({ data: [] }),
          projectsAPI.getMyActionProjects(
            folderId ? { folderId, sortBy } : { sortBy },
          ),
        ]);

      const nextProjects = Array.isArray(projectsResp.data?.items)
        ? projectsResp.data.items
        : [];
      const total = Number(projectsResp.data?.pagination?.total || 0);
      const nextFolders = Array.isArray(foldersResp?.data)
        ? foldersResp.data
        : [];
      const nextMyActionProjects = Array.isArray(myActionResp?.data)
        ? myActionResp.data
        : [];

      setProjects(nextProjects);
      setMyActionProjects(nextMyActionProjects);
      setTotalProjects(total);
      setHasMore(
        nextProjects.length === fetchLimit && nextProjects.length < total,
      );
      setFolders(nextFolders);
      setSelectedProjectIds((current) =>
        current.filter((projectId) =>
          nextProjects.some((project) => project.id === projectId),
        ),
      );
      setCopySelectionIds((current) =>
        current.filter((projectId) =>
          nextProjects.some((project) => project.id === projectId),
        ),
      );
      setActiveFolderId((current) =>
        current && nextFolders.some((folder) => folder.id === current)
          ? current
          : null,
      );
      setCopyTargetFolderId((current) => {
        if (current && nextFolders.some((folder) => folder.id === current)) {
          return current;
        }
        return nextFolders[0]?.id || "";
      });

      const schema = tenantResp?.data?.projectFormSchema || {};
      const fields = Array.isArray(schema.fields) ? schema.fields : [];
      setProjectCardFields(
        fields.filter(
          (field) => field?.key && field?.label && field.showOnProjectCard,
        ),
      );
      setCardFieldOrder(
        Array.isArray(schema.cardFieldOrder) ? schema.cardFieldOrder : null,
      );
      setProjectListFields(
        fields.filter((field) => field?.key && field?.label),
      );
      setInlineCustomFieldValues(
        fields.reduce((acc, field) => {
          acc[field.key] = "";
          return acc;
        }, {}),
      );

      if (pendingRestore) {
        pendingRestoreRef.current = null;
        pendingScrollRef.current = pendingRestore.scrollTop;
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [
    activeFolderId,
    activeTab,
    canViewAll,
    customers,
    limit,
    search,
    sortBy,
    statuses,
    workflows,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedProjectIds([]);
  }, [activeTab, activeFolderId]);

  useEffect(() => {
    saveStoredFilters({
      search,
      statuses,
      workflows,
      customers,
      sortBy,
      activeTab,
      activeFolderId,
      showMyProjectsAtTop,
    });
  }, [
    search,
    sortBy,
    statuses,
    workflows,
    customers,
    activeTab,
    activeFolderId,
    showMyProjectsAtTop,
  ]);

  // Apply the saved offset once the restored rows are actually painted.
  useEffect(() => {
    if (loading) return;
    const target = pendingScrollRef.current;
    if (target == null) return;
    const container = scrollBodyRef.current;
    if (!container) return;
    pendingScrollRef.current = null;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.scrollTop = target;
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [loading, projects.length, listMounted]);

  // Persist offset + loaded count so a return visit can rebuild this view.
  useEffect(() => {
    const container = scrollBodyRef.current;
    if (!container) return;
    let timer = null;
    const persist = () => {
      saveStoredRestore({
        scrollTop: container.scrollTop,
        count: projects.length,
      });
    };
    const onScroll = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        persist();
      }, 150);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    persist();
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [projects.length, listMounted]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    try {
      setLoadingMore(true);
      const folderId =
        activeTab === "folders" && activeFolderId ? activeFolderId : undefined;
      const resp = await projectsAPI.getAll({
        search: search || undefined,
        statuses: statuses.length ? statuses.join(",") : undefined,
        workflows: workflows.length ? workflows.join(",") : undefined,
        customers: customers.length ? customers.join(",") : undefined,
        sortBy,
        folderId,
        skip: projects.length,
        limit,
      });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      const total = Number(resp.data?.pagination?.total || 0);
      setProjects((prev) => [...prev, ...items]);
      setTotalProjects(total);
      setHasMore(
        items.length === limit && projects.length + items.length < total,
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load more projects");
    } finally {
      setLoadingMore(false);
    }
  }, [
    loadingMore,
    hasMore,
    loading,
    projects.length,
    search,
    sortBy,
    statuses,
    workflows,
    customers,
    activeTab,
    activeFolderId,
    limit,
  ]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = scrollBodyRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root: container, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, listMounted]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      document
        .querySelectorAll("details.multi-filter-dropdown[open]")
        .forEach((dropdown) => {
          if (!dropdown.contains(event.target)) {
            dropdown.removeAttribute("open");
          }
        });
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const loadFilterOptions = async () => {
      const [workflowResult, customerResult] = await Promise.allSettled([
        projectsAPI.workflowOptions(),
        customersAPI.getAll(),
      ]);
      setCreateWorkflowOptions(
        workflowResult.status === "fulfilled" &&
          Array.isArray(workflowResult.value?.data)
          ? workflowResult.value.data
          : [],
      );
      setCreateCustomerOptions(
        customerResult.status === "fulfilled" &&
          Array.isArray(customerResult.value?.data)
          ? customerResult.value.data
          : [],
      );
    };
    loadFilterOptions();
  }, []);

  const workflowOptions = useMemo(
    () => createWorkflowOptions.map((w) => w.name),
    [createWorkflowOptions],
  );

  const customerOptions = useMemo(
    () => createCustomerOptions.map((c) => c.name),
    [createCustomerOptions],
  );

  const activeFolder = useMemo(
    () => folders.find((folder) => folder.id === activeFolderId) || null,
    [folders, activeFolderId],
  );

  const getMySubmittedAwaitingReview = useCallback(
    (project) => {
      if (!user?.id) return false;
      const isAssignee = (project.currentStageAssignments || []).some(
        (item) => item.assignee?.id === user.id,
      );
      if (!isAssignee) return false;
      const submittedAssignment = (project.currentStageAssignments || []).find(
        (item) => item.status === "SUBMITTED",
      );
      if (!submittedAssignment) return false;
      const stage = (project.workflow?.definition?.stages || []).find(
        (item) => item.id === submittedAssignment.stageId,
      );
      return stage?.sendForReview !== false;
    },
    [user?.id],
  );

  const myActionProjectIds = useMemo(
    () => new Set(myActionProjects.map((p) => p.id)),
    [myActionProjects],
  );

  // "Do Tasks" only renders for own non-submitted assignments, and never on a
  // held or awaiting-invoice project — mirror that here so the ordering matches
  // the buttons the card actually shows.
  const hasDoTaskAction = useCallback(
    (project) => {
      if (!user?.id) return false;
      if (["ON_HOLD", "AWAITING_INVOICE"].includes(project.status))
        return false;
      return (project.currentStageAssignments || []).some(
        (item) => item.assignee?.id === user.id && item.status !== "SUBMITTED",
      );
    },
    [user?.id],
  );

  // Actionable work first; the server sort order is preserved inside each group.
  const sortedMyActionProjects = useMemo(() => {
    const withTask = [];
    const withoutTask = [];
    myActionProjects.forEach((project) => {
      if (hasDoTaskAction(project)) withTask.push(project);
      else withoutTask.push(project);
    });
    return [...withTask, ...withoutTask];
  }, [myActionProjects, hasDoTaskAction]);

  const otherProjects = useMemo(() => projects, [projects]);

  const visibleFolders = useMemo(() => {
    const searchValue = folderSearch.toLowerCase();
    return folders.filter((folder) => {
      return !folderSearch || folder.name.toLowerCase().includes(searchValue);
    });
  }, [folders, folderSearch]);

  const copyCandidates = useMemo(() => {
    const searchValue = copySearch.toLowerCase();
    return allProjectsForCopy.filter((project) => {
      const notInCurrentFolder = Array.isArray(project.folders)
        ? !project.folders.some((folder) => folder.id === activeFolderId)
        : true;
      const bySearch =
        !copySearch ||
        project.name.toLowerCase().includes(searchValue) ||
        project.customer?.name?.toLowerCase().includes(searchValue) ||
        project.workflow?.name?.toLowerCase().includes(searchValue);
      return notInCurrentFolder && bySearch;
    });
  }, [allProjectsForCopy, activeFolderId, copySearch]);

  const selectedCount = selectedProjectIds.length;

  const toggleFromMultiSelect = (setter, value) => {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };

  const getFilterLabel = (selectedValues, allOptions, fallback) => {
    if (!selectedValues.length) return fallback;
    const labels = allOptions
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  };

  const toggleProjectSelection = (projectId, checked) => {
    setSelectedProjectIds((current) => {
      if (checked) return Array.from(new Set([...current, projectId]));
      return current.filter((id) => id !== projectId);
    });
  };

  const toggleCopySelection = (projectId, checked) => {
    setCopySelectionIds((current) => {
      if (checked) return Array.from(new Set([...current, projectId]));
      return current.filter((id) => id !== projectId);
    });
  };

  const archiveProject = (project) => {
    if (!canDelete) return;
    setConfirm({
      title: "Archive Project",
      message: `Archive project "${project.name}"?`,
      confirmLabel: "Archive",
      confirmClass: "btn-danger",
      onConfirm: async () => {
        try {
          await projectsAPI.delete(project.id);
          await load();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to archive project");
        }
      },
    });
  };

  const deleteProject = (project) => {
    if (!canDelete) return;
    setConfirm({
      title: "Delete Project",
      message: `Delete project "${project.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      confirmClass: "btn-danger",
      onConfirm: async () => {
        try {
          await projectsAPI.delete(project.id);
          await load();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to delete project");
        }
      },
    });
  };

  const putProjectOnHold = (project) => {
    setConfirm({
      title: "Put Project On Hold",
      message: (
        <>
          Are you sure you want to put <strong>{project.name}</strong> on hold?
          It can be resumed at any time.
        </>
      ),
      confirmLabel: "Put On Hold",
      confirmClass: "btn-danger",
      onConfirm: async () => {
        try {
          setError("");
          await projectsAPI.updateStatus(project.id, "ON_HOLD");
          await load();
        } catch (err) {
          setError(
            err.response?.data?.message || "Failed to put project on hold",
          );
        }
      },
    });
  };

  const flagProjectForInvoicing = (project) => {
    setConfirm({
      title: "Flag for Invoicing",
      message: `Flag "${project.name}" for invoicing?`,
      confirmLabel: "Flag for Invoicing",
      confirmClass: "btn-primary",
      onConfirm: async () => {
        try {
          setError("");
          await projectsAPI.flagForInvoicing(project.id);
          await load();
        } catch (err) {
          setError(
            err.response?.data?.message || "Failed to flag for invoicing",
          );
        }
      },
    });
  };

  const resumeProject = (project) => {
    const nextStatus = getCurrentStageNames(project).length
      ? "IN_PROGRESS"
      : "PENDING";
    setConfirm({
      title: "Resume Project",
      message: `Resume project "${project.name}"?`,
      confirmLabel: "Resume",
      confirmClass: "btn-primary",
      onConfirm: async () => {
        try {
          setError("");
          await projectsAPI.updateStatus(project.id, nextStatus);
          await load();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to resume project");
        }
      },
    });
  };

  const submitFolderCreate = async (event) => {
    event.preventDefault();
    const name = folderForm.name.trim();
    if (!name) {
      setError("Folder name is required");
      return;
    }
    try {
      setSavingFolder(true);
      setError("");
      await projectsAPI.createFolder({
        name,
        description: folderForm.description.trim() || null,
      });
      setFolderModalOpen(false);
      setFolderForm({ name: "", description: "" });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create folder");
    } finally {
      setSavingFolder(false);
    }
  };

  const renameFolder = async (folder) => {
    const nextName = window.prompt("Enter the new folder name", folder.name);
    if (nextName === null) return;
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === folder.name) return;
    try {
      setError("");
      await projectsAPI.updateFolder(folder.id, { name: trimmed });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to rename folder");
    }
  };

  const deleteFolder = (folder) => {
    setConfirm({
      title: "Delete Folder",
      message: `Delete folder "${folder.name}"?`,
      confirmLabel: "Delete",
      confirmClass: "btn-danger",
      onConfirm: async () => {
        try {
          setError("");
          await projectsAPI.deleteFolder(folder.id);
          if (activeFolderId === folder.id) {
            setActiveFolderId(null);
          }
          await load();
        } catch (err) {
          setError(err.response?.data?.message || "Failed to delete folder");
        }
      },
    });
  };

  const copySelectedToFolder = async () => {
    if (!selectedProjectIds.length || !copyTargetFolderId) return;
    try {
      setError("");
      await projectsAPI.moveProjectsToFolder(
        selectedProjectIds,
        copyTargetFolderId,
      );
      setSelectedProjectIds([]);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to copy projects");
    }
  };

  const removeSelectedFromFolder = async () => {
    if (!selectedProjectIds.length || !activeFolderId) return;
    try {
      setError("");
      await projectsAPI.removeProjectsFromFolder(
        activeFolderId,
        selectedProjectIds,
      );
      setSelectedProjectIds([]);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to remove from folder");
    }
  };

  const copyProjectsIntoCurrentFolder = async () => {
    if (!activeFolderId || !copySelectionIds.length) return;
    try {
      setCopyingToFolder(true);
      setError("");
      await projectsAPI.moveProjectsToFolder(copySelectionIds, activeFolderId);
      setCopySelectionIds([]);
      setCopyModalOpen(false);
      setCopySearch("");
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to copy to folder");
    } finally {
      setCopyingToFolder(false);
    }
  };

  const createProjectInline = async () => {
    const name = inlineCreateForm.name.trim();
    if (!name) {
      setError("Project name is required");
      return;
    }
    if (!inlineCreateForm.customerId) {
      setError("Customer is required");
      return;
    }
    if (!inlineCreateForm.workflowId) {
      setError("Workflow is required");
      return;
    }
    const requiredMissing = projectListFields.filter((field) => {
      if (!field.required) return false;
      const value = inlineCustomFieldValues[field.key];
      return !String(value || "").trim();
    });
    if (requiredMissing.length) {
      setError("Please fill all required custom fields.");
      return;
    }

    try {
      setCreatingInline(true);
      setError("");
      const payload = new FormData();
      payload.append("name", name);
      payload.append("description", "");
      payload.append("customerId", inlineCreateForm.customerId);
      payload.append("workflowId", inlineCreateForm.workflowId);
      payload.append("durationValue", "1");
      payload.append("durationUnit", "DAYS");
      payload.append(
        "customFields",
        JSON.stringify(inlineCustomFieldValues || {}),
      );
      await projectsAPI.create(payload);
      setInlineAddOpen(false);
      setInlineCreateForm({
        name: "",
        customerId: "",
        workflowId: "",
      });
      setInlineCustomFieldValues(
        projectListFields.reduce((acc, field) => {
          acc[field.key] = "";
          return acc;
        }, {}),
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create project");
    } finally {
      setCreatingInline(false);
    }
  };

  const handleDownloadReport = async (startDate, endDate, selectedFields) => {
    setShowDownloadModal(false);
    setDownloadLoading(true);
    try {
      const folderId =
        activeTab === "folders" && activeFolderId ? activeFolderId : undefined;
      const resp = await projectsAPI.getAll({
        search: search || undefined,
        statuses: statuses.length ? statuses.join(",") : undefined,
        workflows: workflows.length ? workflows.join(",") : undefined,
        customers: customers.length ? customers.join(",") : undefined,
        sortBy,
        folderId,
        updatedAfter: startDate,
        updatedBefore: endDate,
        skip: 0,
        limit: 5000,
      });
      const projectIds = (resp.data?.items || []).map((p) => String(p.id));
      const params = new URLSearchParams({ startDate, endDate });
      params.set("projectIds", projectIds.join(","));
      if (selectedFields?.length)
        params.set("fields", selectedFields.join(","));
      const dlResp = await api.get(`/projects/overview-report?${params}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([dlResp.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      const label =
        startDate === endDate ? startDate : `${startDate}_${endDate}`;
      a.href = url;
      a.download = `projects-report-${label}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    } finally {
      setDownloadLoading(false);
    }
  };

  const showProjectsTab = activeTab === "projects";
  const showFoldersTab = activeTab === "folders";
  const showFolderDetail = showFoldersTab && !!activeFolderId;
  const viewMode =
    listMounted && requestedView === "gantt" ? "card" : requestedView;
  const showGantt = !listMounted;

  const formatCellValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.join(", ") || "-";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const renderProjectTable = (
    items,
    { showInlineCreate = false, actionProjectIds = null } = {},
  ) => {
    const renderInlineCustomFieldInput = (field) => {
      const currentValue = inlineCustomFieldValues[field.key] ?? "";
      const onChange = (value) =>
        setInlineCustomFieldValues((current) => ({
          ...current,
          [field.key]: value,
        }));

      if (field.type === "select" && Array.isArray(field.options)) {
        return (
          <select
            value={currentValue}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">Select</option>
            {field.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      }

      const inputType =
        field.type === "number"
          ? "number"
          : field.type === "date"
            ? "date"
            : "text";
      return (
        <input
          type={inputType}
          value={currentValue}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.required ? "Required" : ""}
        />
      );
    };

    return (
      <div className="project-list-table-wrap">
        <table className="project-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Customer</th>
              <th>Workflow</th>
              <th>Current Stage</th>
              {projectListFields.map((field) => (
                <th key={field.key}>{field.label}</th>
              ))}
              <th>Created At</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {showInlineCreate && inlineAddOpen && (
              <tr>
                <td>
                  <input
                    value={inlineCreateForm.name}
                    onChange={(event) =>
                      setInlineCreateForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="Project name"
                  />
                </td>
                <td>{formatProjectStatus("PENDING")}</td>
                <td>
                  <select
                    value={inlineCreateForm.customerId}
                    onChange={(event) =>
                      setInlineCreateForm((current) => ({
                        ...current,
                        customerId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select customer</option>
                    {createCustomerOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={inlineCreateForm.workflowId}
                    onChange={(event) =>
                      setInlineCreateForm((current) => ({
                        ...current,
                        workflowId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select workflow</option>
                    {createWorkflowOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>-</td>
                {projectListFields.map((field) => (
                  <td key={`inline-${field.key}`}>
                    {renderInlineCustomFieldInput(field)}
                  </td>
                ))}
                <td>-</td>
                <td>-</td>
                <td>
                  <div className="project-list-actions">
                    <button
                      className="btn-primary btn-sm"
                      onClick={createProjectInline}
                      disabled={creatingInline}
                    >
                      {creatingInline ? "Adding..." : "Add"}
                    </button>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => setInlineAddOpen(false)}
                      disabled={creatingInline}
                    >
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {items.map((project) => (
              <tr
                key={project.id}
                style={{ background: getRowBackground(project.status) }}
              >
                <td>
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {formatCellValue(project.name)}
                    {actionProjectIds?.has(project.id) && (
                      <span className="my-action-badge">My Action</span>
                    )}
                  </span>
                </td>
                <td>{formatProjectStatus(project.status)}</td>
                <td>{formatCellValue(project.customer?.name)}</td>
                <td>{formatCellValue(project.workflow?.name)}</td>
                <td>
                  {formatCellValue(
                    getCurrentStageNames(project).join(", ") || null,
                  )}
                </td>
                {projectListFields.map((field) => (
                  <td key={`${project.id}-${field.key}`}>
                    {formatCellValue(project.customFields?.[field.key])}
                  </td>
                ))}
                <td>{formatDate(project.createdAt)}</td>
                <td>{formatDate(project.updatedAt)}</td>
                <td>
                  <div className="project-list-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      Open
                    </button>
                    {canCreate && (
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => setEditProject(project)}
                      >
                        Edit
                      </button>
                    )}
                    {project.status === "ON_HOLD" ? (
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => resumeProject(project)}
                      >
                        Resume
                      </button>
                    ) : (
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => putProjectOnHold(project)}
                      >
                        Hold
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="btn-danger btn-sm"
                        onClick={() => deleteProject(project)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="projects-page">
      <div className="projects-top-bar">
        <div className="page-header">
          <h1 style={{ margin: 0 }}>Projects</h1>
          <div className="project-page-actions">
            {canAssignUsers && (
              <button
                className="btn-secondary"
                onClick={() => setShowDownloadModal(true)}
                disabled={downloadLoading}
              >
                {downloadLoading ? "Generating..." : "Download Report"}
              </button>
            )}
            {showProjectsTab &&
              canCreate &&
              (viewMode === "list" ? (
                <button
                  className="btn-primary"
                  onClick={() => setInlineAddOpen((current) => !current)}
                >
                  {inlineAddOpen ? "Cancel Row" : "+ Add Row"}
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={() => setAddOpen(true)}
                >
                  + Add Project
                </button>
              ))}
            {showFoldersTab && canViewAll && (
              <button
                className="btn-secondary"
                onClick={() => setFolderModalOpen(true)}
              >
                + Add Folder
              </button>
            )}
            {(showProjectsTab || showFolderDetail) && (
              <ViewModeSwitch
                modes={showProjectsTab ? PROJECT_VIEW_MODES : FOLDER_VIEW_MODES}
                value={viewMode}
                onChange={setViewMode}
                ariaLabel="Projects view"
              />
            )}
          </div>
        </div>

        <div className="project-filters">
          <input
            placeholder="Search projects..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <details className="multi-filter-dropdown">
            <summary>
              {getFilterLabel(statuses, STATUS_OPTIONS, "All Statuses")}
            </summary>
            <div className="multi-filter-menu">
              {STATUS_OPTIONS.map((option) => (
                <label key={option.value} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={statuses.includes(option.value)}
                    onChange={() =>
                      toggleFromMultiSelect(setStatuses, option.value)
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </details>
          <details className="multi-filter-dropdown">
            <summary>
              {getFilterLabel(
                workflows,
                workflowOptions.map((item) => ({ value: item, label: item })),
                "All Workflows",
              )}
            </summary>
            <div className="multi-filter-menu">
              {workflowOptions.map((item) => (
                <label key={item} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={workflows.includes(item)}
                    onChange={() => toggleFromMultiSelect(setWorkflows, item)}
                  />
                  {item}
                </label>
              ))}
            </div>
          </details>
          <details className="multi-filter-dropdown">
            <summary>
              {getFilterLabel(
                customers,
                customerOptions.map((item) => ({ value: item, label: item })),
                "All Customers",
              )}
            </summary>
            <div className="multi-filter-menu">
              {customerOptions.map((item) => (
                <label key={item} className="multi-filter-item">
                  <input
                    type="checkbox"
                    checked={customers.includes(item)}
                    onChange={() => toggleFromMultiSelect(setCustomers, item)}
                  />
                  {item}
                </label>
              ))}
            </div>
          </details>
          <button
            className="btn-secondary btn-sm"
            onClick={() =>
              setSortBy((current) => {
                if (current === "updatedAt") return "name";
                if (current === "name") return "nameDesc";
                return "updatedAt";
              })
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            {sortBy === "updatedAt"
              ? "Sort: Latest"
              : sortBy === "name"
                ? "Sort: A-Z"
                : "Sort: Z-A"}
          </button>
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              setSearch("");
              setStatuses([]);
              setWorkflows([]);
              setCustomers([]);
            }}
          >
            Clear Filters
          </button>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {canViewAll && (
            <div className="projects-tabs">
              <button
                className={`projects-tab-btn ${showProjectsTab ? "active" : ""}`}
                onClick={() => setActiveTab("projects")}
              >
                Projects
              </button>
              <button
                className={`projects-tab-btn ${showFoldersTab ? "active" : ""}`}
                onClick={() => setActiveTab("folders")}
              >
                Folders
              </button>
            </div>
          )}
          <label
            className={`my-projects-toggle${showMyProjectsAtTop ? " active" : ""}`}
            style={{ marginLeft: "auto" }}
          >
            <input
              type="checkbox"
              checked={showMyProjectsAtTop}
              onChange={(event) => setShowMyProjectsAtTop(event.target.checked)}
            />
            Show My Projects
          </label>
        </div>
      </div>

      {showGantt ? (
        <div className="projects-gantt-body">
          <PortfolioGanttChart
            filters={{ search, statuses, workflows, customers }}
          />
        </div>
      ) : (
        <div
          className={`projects-scroll-body${viewMode === "list" ? " list-view-mode" : ""}`}
          ref={scrollBodyRef}
        >
          {showProjectsTab && canViewAll && selectedCount > 0 && (
            <div className="folder-bulk-move panel">
              {selectedCount > 0 && (
                <span className="small-muted">{selectedCount} selected</span>
              )}
              {folders.length ? (
                <>
                  {selectedCount > 0 && (
                    <>
                      <select
                        value={copyTargetFolderId}
                        onChange={(event) =>
                          setCopyTargetFolderId(event.target.value)
                        }
                      >
                        {folders.map((folder) => (
                          <option key={folder.id} value={folder.id}>
                            {folder.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn-primary btn-sm"
                        onClick={copySelectedToFolder}
                      >
                        Copy to Folder
                      </button>
                    </>
                  )}
                  {selectedCount > 0 && (
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => setSelectedProjectIds([])}
                    >
                      Clear Selection
                    </button>
                  )}
                </>
              ) : (
                <span className="small-muted">
                  Create at least one folder to copy selected projects.
                </span>
              )}
            </div>
          )}

          {showFoldersTab && !showFolderDetail && (
            <div className="folder-list-panel panel">
              <div className="folder-list-header">
                <strong>Folders</strong>
                <input
                  className="folder-search-input"
                  placeholder="Search folders..."
                  value={folderSearch}
                  onChange={(event) => setFolderSearch(event.target.value)}
                />
              </div>
              <div className="folder-list">
                {visibleFolders.length ? (
                  visibleFolders.map((folder) => (
                    <div key={folder.id} className="folder-row">
                      <button
                        className="folder-avatar"
                        onClick={() => setActiveFolderId(folder.id)}
                        aria-label={`Open ${folder.name}`}
                      >
                        FL
                      </button>
                      <div className="folder-main">
                        <button
                          className="folder-name-btn"
                          onClick={() => setActiveFolderId(folder.id)}
                        >
                          {folder.name}
                        </button>
                        <div className="folder-meta-stack">
                          <div className="folder-meta-item">
                            Created by: {formatUserName(folder.createdBy)}
                          </div>
                          <div className="folder-meta-item">
                            Created at: {formatDate(folder.createdAt)}
                          </div>
                          <div className="folder-meta-item">
                            Updated at: {formatDate(folder.updatedAt)}
                          </div>
                        </div>
                      </div>
                      <div className="folder-row-right">
                        <div className="folder-status-counts">
                          <span className="folder-status-chip folder-status-total">
                            Total: {folder.projectCount ?? 0}
                          </span>
                          <span className="folder-status-chip folder-status-pending">
                            Pending: {folder.statusCounts?.PENDING ?? 0}
                          </span>
                          <span className="folder-status-chip folder-status-inprogress">
                            In Progress: {folder.statusCounts?.IN_PROGRESS ?? 0}
                          </span>
                          <span className="folder-status-chip folder-status-onhold">
                            On Hold: {folder.statusCounts?.ON_HOLD ?? 0}
                          </span>
                          <span className="folder-status-chip folder-status-completed">
                            Done: {folder.statusCounts?.COMPLETED ?? 0}
                          </span>
                        </div>
                        <div className="folder-row-actions">
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => renameFolder(folder)}
                          >
                            Rename
                          </button>
                          <button
                            className="btn-danger btn-sm"
                            onClick={() => deleteFolder(folder)}
                          >
                            Delete
                          </button>
                          <button
                            className="btn-primary btn-sm"
                            onClick={() => setActiveFolderId(folder.id)}
                          >
                            Open
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="small-muted">No folders found.</div>
                )}
              </div>
            </div>
          )}

          {showFolderDetail && (
            <div className="folder-detail-header panel">
              <div>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setActiveFolderId(null);
                    setSelectedProjectIds([]);
                  }}
                >
                  Back to Folders
                </button>
                <h3 style={{ margin: "10px 0 4px" }}>
                  {activeFolder?.name || "Folder"}
                </h3>
                <div className="small-muted">
                  {activeFolder ? `${activeFolder.projectCount} projects` : ""}
                </div>
              </div>
              <div className="folder-detail-actions">
                <button
                  className="btn-primary btn-sm"
                  onClick={async () => {
                    setCopySelectionIds([]);
                    setCopySearch("");
                    setCopyModalOpen(true);
                    try {
                      setLoadingCopyProjects(true);
                      const resp = await projectsAPI.getAll({ limit: 1000 });
                      setAllProjectsForCopy(
                        Array.isArray(resp.data?.items) ? resp.data.items : [],
                      );
                    } catch {
                      setAllProjectsForCopy([]);
                    } finally {
                      setLoadingCopyProjects(false);
                    }
                  }}
                >
                  Copy Projects to This Folder
                </button>
                {activeFolder && (
                  <button
                    className="btn-danger btn-sm"
                    onClick={() => deleteFolder(activeFolder)}
                  >
                    Delete Folder
                  </button>
                )}
                {selectedCount > 0 && (
                  <>
                    <button
                      className="btn-danger btn-sm"
                      onClick={removeSelectedFromFolder}
                    >
                      Remove from Folder
                    </button>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => setSelectedProjectIds([])}
                    >
                      Clear Selection
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="small-muted" style={{ color: "#b91c1c" }}>
              {error}
            </div>
          )}

          {loading ? (
            <div className="small-muted">Loading projects...</div>
          ) : (
            <div className="projects-list">
              {showProjectsTab && (
                <>
                  {viewMode === "card" ? (
                    <>
                      {showMyProjectsAtTop &&
                        sortedMyActionProjects.map((project) => (
                          <ProjectCard
                            key={project.id}
                            project={project}
                            mySubmittedAwaitingReview={getMySubmittedAwaitingReview(
                              project,
                            )}
                            projectCardFields={projectCardFields}
                            cardFieldOrder={cardFieldOrder}
                            workAssignments={
                              project.currentStageAssignments?.filter(
                                (item) =>
                                  item.assignee?.id === user?.id &&
                                  item.status !== "SUBMITTED",
                              ) || []
                            }
                            onEdit={
                              canCreate
                                ? () => setEditProject(project)
                                : undefined
                            }
                            onHold={() => putProjectOnHold(project)}
                            onResume={() => resumeProject(project)}
                            onStatusForced={load}
                            onArchive={() => archiveProject(project)}
                            onDelete={() => deleteProject(project)}
                            onFlagForInvoicing={() =>
                              flagProjectForInvoicing(project)
                            }
                            showSelection={canViewAll}
                            isSelected={selectedProjectIds.includes(project.id)}
                            onSelectionChange={toggleProjectSelection}
                            showFolder={false}
                            showFolderMembership
                          />
                        ))}
                      {showMyProjectsAtTop &&
                        myActionProjects.length > 0 &&
                        otherProjects.length > 0 && (
                          <div className="projects-section-divider">
                            <span>All Projects</span>
                          </div>
                        )}
                      {otherProjects.map((project) => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          mySubmittedAwaitingReview={getMySubmittedAwaitingReview(
                            project,
                          )}
                          projectCardFields={projectCardFields}
                          cardFieldOrder={cardFieldOrder}
                          workAssignments={
                            project.currentStageAssignments?.filter(
                              (item) =>
                                item.assignee?.id === user?.id &&
                                item.status !== "SUBMITTED",
                            ) || []
                          }
                          onEdit={
                            canCreate
                              ? () => setEditProject(project)
                              : undefined
                          }
                          onHold={() => putProjectOnHold(project)}
                          onResume={() => resumeProject(project)}
                          onStatusForced={load}
                          onArchive={() => archiveProject(project)}
                          onDelete={() => deleteProject(project)}
                          onFlagForInvoicing={() =>
                            flagProjectForInvoicing(project)
                          }
                          showSelection={canViewAll}
                          isSelected={selectedProjectIds.includes(project.id)}
                          onSelectionChange={toggleProjectSelection}
                          showFolder={false}
                          showFolderMembership
                        />
                      ))}
                      {!projects.length && (
                        <div className="small-muted">No projects found.</div>
                      )}
                    </>
                  ) : (
                    <>
                      {renderProjectTable(
                        showMyProjectsAtTop
                          ? [
                              ...sortedMyActionProjects,
                              ...otherProjects.filter(
                                (p) => !myActionProjectIds.has(p.id),
                              ),
                            ]
                          : otherProjects,
                        {
                          showInlineCreate: inlineAddOpen,
                          actionProjectIds: showMyProjectsAtTop
                            ? myActionProjectIds
                            : null,
                        },
                      )}
                      {!myActionProjects.length && !otherProjects.length && (
                        <div className="small-muted">No projects found.</div>
                      )}
                    </>
                  )}
                </>
              )}
              {showFolderDetail && (
                <>
                  {viewMode === "card" ? (
                    <>
                      {showMyProjectsAtTop &&
                        sortedMyActionProjects.map((project) => (
                          <ProjectCard
                            key={project.id}
                            project={project}
                            mySubmittedAwaitingReview={getMySubmittedAwaitingReview(
                              project,
                            )}
                            projectCardFields={projectCardFields}
                            cardFieldOrder={cardFieldOrder}
                            workAssignments={
                              project.currentStageAssignments?.filter(
                                (item) =>
                                  item.assignee?.id === user?.id &&
                                  item.status !== "SUBMITTED",
                              ) || []
                            }
                            onEdit={
                              canCreate
                                ? () => setEditProject(project)
                                : undefined
                            }
                            onHold={() => putProjectOnHold(project)}
                            onResume={() => resumeProject(project)}
                            onStatusForced={load}
                            onArchive={() => archiveProject(project)}
                            onDelete={() => deleteProject(project)}
                            onFlagForInvoicing={() =>
                              flagProjectForInvoicing(project)
                            }
                            showSelection={canViewAll}
                            isSelected={selectedProjectIds.includes(project.id)}
                            onSelectionChange={toggleProjectSelection}
                            showFolder={false}
                            showFolderMembership
                          />
                        ))}
                      {showMyProjectsAtTop &&
                        myActionProjects.length > 0 &&
                        otherProjects.length > 0 && (
                          <div className="projects-section-divider">
                            <span>All Projects</span>
                          </div>
                        )}
                      {otherProjects.map((project) => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          mySubmittedAwaitingReview={getMySubmittedAwaitingReview(
                            project,
                          )}
                          projectCardFields={projectCardFields}
                          cardFieldOrder={cardFieldOrder}
                          workAssignments={
                            project.currentStageAssignments?.filter(
                              (item) =>
                                item.assignee?.id === user?.id &&
                                item.status !== "SUBMITTED",
                            ) || []
                          }
                          onEdit={
                            canCreate
                              ? () => setEditProject(project)
                              : undefined
                          }
                          onHold={() => putProjectOnHold(project)}
                          onResume={() => resumeProject(project)}
                          onStatusForced={load}
                          onArchive={() => archiveProject(project)}
                          onDelete={() => deleteProject(project)}
                          onFlagForInvoicing={() =>
                            flagProjectForInvoicing(project)
                          }
                          showSelection={canViewAll}
                          isSelected={selectedProjectIds.includes(project.id)}
                          onSelectionChange={toggleProjectSelection}
                          showFolder={false}
                          showFolderMembership
                        />
                      ))}
                      {!projects.length && (
                        <div className="small-muted">
                          No projects in this folder.
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {renderProjectTable(
                        showMyProjectsAtTop
                          ? [
                              ...sortedMyActionProjects,
                              ...otherProjects.filter(
                                (p) => !myActionProjectIds.has(p.id),
                              ),
                            ]
                          : otherProjects,
                        {
                          actionProjectIds: showMyProjectsAtTop
                            ? myActionProjectIds
                            : null,
                        },
                      )}
                      {!myActionProjects.length && !otherProjects.length && (
                        <div className="small-muted">
                          No projects in this folder.
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {showFoldersTab && !showFolderDetail && null}
            </div>
          )}
          <div ref={sentinelRef} />
          {loadingMore && (
            <div
              className="small-muted"
              style={{ textAlign: "center", padding: "12px 0" }}
            >
              Loading more...
            </div>
          )}
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={() => {
            const action = confirm.onConfirm;
            setConfirm(null);
            action();
          }}
          onClose={() => setConfirm(null)}
        />
      )}

      {showDownloadModal && (
        <ProjectDownloadModal
          customFieldKeys={projectListFields}
          onConfirm={handleDownloadReport}
          onClose={() => setShowDownloadModal(false)}
        />
      )}

      <AddProjectModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={load}
      />
      <EditProjectModal
        open={!!editProject}
        project={editProject}
        onClose={() => setEditProject(null)}
        onUpdated={load}
      />

      {folderModalOpen && (
        <div
          className="modal-backdrop"
          onClick={() => setFolderModalOpen(false)}
        >
          <div
            className="modal-content folder-create-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Create Folder</h3>
            <form className="modal-grid" onSubmit={submitFolderCreate}>
              <input
                placeholder="Folder name"
                value={folderForm.name}
                onChange={(event) =>
                  setFolderForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                maxLength={120}
                required
              />
              <textarea
                placeholder="Description (optional)"
                value={folderForm.description}
                onChange={(event) =>
                  setFolderForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
                rows={3}
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setFolderModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingFolder}
                >
                  {savingFolder ? "Creating..." : "Create Folder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {copyModalOpen && (
        <div className="modal-backdrop" onClick={() => setCopyModalOpen(false)}>
          <div
            className="modal-content folder-copy-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>
              Copy Projects to {activeFolder?.name || "Folder"}
            </h3>
            <input
              placeholder="Search projects..."
              value={copySearch}
              onChange={(event) => setCopySearch(event.target.value)}
            />
            <div className="folder-copy-list">
              {loadingCopyProjects ? (
                <div className="small-muted">Loading projects...</div>
              ) : copyCandidates.length ? (
                copyCandidates.map((project) => {
                  const selected = copySelectionIds.includes(project.id);
                  return (
                    <div
                      key={project.id}
                      className={`folder-copy-item${selected ? " folder-copy-item--selected" : ""}`}
                      onClick={() => toggleCopySelection(project.id, !selected)}
                    >
                      {project.name}
                    </div>
                  );
                })
              ) : (
                <div className="small-muted">
                  No projects available to copy.
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setCopyModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={copyProjectsIntoCurrentFolder}
                disabled={!copySelectionIds.length || copyingToFolder}
              >
                {copyingToFolder ? "Copying..." : "Copy Selected"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Local, not UTC — toISOString() reads back a day early for any timezone ahead
// of UTC, defaulting the report range to yesterday.
function todayStr() {
  return todayString();
}

const PROJECT_FIELDS = [
  { key: "customerName", label: "Customer Name" },
  { key: "workflowName", label: "Workflow" },
  { key: "status", label: "Project Status" },
  { key: "hoursLogged", label: "Hours Logged" },
];

function CheckRow({ checked, onChange, label }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        fontSize: 13,
        padding: "6px 8px",
        borderRadius: 6,
        background: checked ? "#eff6ff" : "#f9fafb",
        border: "1px solid",
        borderColor: checked ? "#bfdbfe" : "#e5e7eb",
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span style={{ flex: 1, fontWeight: 500 }}>{label}</span>
    </label>
  );
}

function ProjectDownloadModal({ customFieldKeys = [], onConfirm, onClose }) {
  const allStaticFieldKeys = PROJECT_FIELDS.map((f) => f.key);
  const allCustomFieldKeys = customFieldKeys.map((f) => `customField:${f.key}`);
  const allFieldKeys = [...allStaticFieldKeys, ...allCustomFieldKeys];

  const [selectedFields, setSelectedFields] = useState(new Set(allFieldKeys));
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const toggleField = (key) => {
    const next = new Set(selectedFields);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedFields(next);
  };

  const allFieldsChecked = selectedFields.size === allFieldKeys.length;

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content" style={{ maxWidth: 480 }}>
        <div className="um-dialog-head">
          <h2>Download Report</h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div
          className="um-dialog-body"
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 10,
              }}
            >
              Date Range
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 13, color: "#64748b" }}>From</label>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  flex: 1,
                  fontSize: 13,
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                }}
              />
              <label style={{ fontSize: 13, color: "#64748b" }}>To</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  flex: 1,
                  fontSize: 13,
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                Project Fields
              </span>
              <button
                type="button"
                className="uar-toggle-btn uar-toggle-btn--active"
                style={{ fontSize: 12, padding: "2px 10px" }}
                onClick={() =>
                  setSelectedFields(
                    allFieldsChecked ? new Set() : new Set(allFieldKeys),
                  )
                }
              >
                {allFieldsChecked ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {PROJECT_FIELDS.map((f) => (
                <CheckRow
                  key={f.key}
                  checked={selectedFields.has(f.key)}
                  onChange={() => toggleField(f.key)}
                  label={f.label}
                />
              ))}
              {customFieldKeys.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      marginTop: 4,
                      marginBottom: 2,
                      paddingLeft: 2,
                    }}
                  >
                    Custom Fields
                  </div>
                  {customFieldKeys.map((f) => (
                    <CheckRow
                      key={`customField:${f.key}`}
                      checked={selectedFields.has(`customField:${f.key}`)}
                      onChange={() => toggleField(`customField:${f.key}`)}
                      label={f.label}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="um-dialog-footer">
          <button
            className="um-btn um-btn-primary"
            onClick={() =>
              onConfirm(startDate, endDate, Array.from(selectedFields))
            }
          >
            Download PDF
          </button>
          <button
            className="um-btn um-btn-secondary"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
